import { useState, useEffect } from 'react';
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { TagForm } from '../../components/tags';
import { createTag, ApiError } from '../../lib/apiClient';
import type { TagFormData } from '../../components/tags/TagForm';
import type { TagCategory } from '../../graphql/types';
import '@tylertech/forge/icon';
import '@tylertech/forge/banner';

const API_BASE_URL = import.meta.env.VITE_REST_API_URL || 'http://localhost:3001/api/v1';

export const Route = createFileRoute('/tags/new')({
  component: CreateTagPage,
});

export { CreateTagPage };

function CreateTagPage(): JSX.Element {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<TagCategory[]>([]);

  const IconElement = 'forge-icon' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  useEffect(() => {
    fetch(`${API_BASE_URL}/tag-categories`, { credentials: 'include' })
      .then((res) => (res.ok ? (res.json() as Promise<{ data: TagCategory[] }>) : { data: [] }))
      .then((json) => setCategories((json as { data: TagCategory[] }).data))
      .catch(() => setCategories([]));
  }, []);

  const handleSubmit = async (data: TagFormData): Promise<void> => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createTag({
        name: data.name,
        categoryId: data.categoryId,
        color: data.color || undefined,
        description: data.description || undefined,
        value: data.value,
      });
      // Land on the category the new tag was created in (#715 — the list page
      // no longer has an "All Categories" view, so an empty categoryId would
      // default to the first-listed category, which may not be this one and
      // would make the just-created tag appear to vanish).
      void navigate({
        to: '/tags',
        search: { page: 1, pageSize: 25, categoryId: data.categoryId },
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsSubmitting(false);
    }
  };

  const handleCancel = (): void => {
    void navigate({ to: '/tags', search: { page: 1, pageSize: 25, categoryId: '' } });
  };

  return (
    <div className="create-tag-page">
      <div className="breadcrumb">
        <Link
          to="/tags"
          search={{ page: 1, pageSize: 25, categoryId: '' }}
          className="breadcrumb-link"
        >
          <IconElement name="arrow_back" aria-hidden="true" />
          <span>Back to Tags</span>
        </Link>
      </div>

      <div className="page-header">
        <h1 className="forge-typography--headline4">Create Tag</h1>
      </div>

      {error && (
        <BannerElement theme="error" dismissed={false} className="error-banner" role="alert">
          <div className="banner-content">
            <IconElement name="error" aria-hidden="true" />
            <span>{error}</span>
          </div>
        </BannerElement>
      )}

      <TagForm
        mode="create"
        categories={categories}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
